import styles from "./Error.module.css"
import errorImage from "../../asset/images/errorImage.png";
import { useEffect } from 'react';
import { trackCustomEvent, CUSTOM_EVENTS, ANALYTICS_PROVIDERS } from '../../utils/analyticsService';


export const ErrorFallback = ({ error, errorInfo, errorDetails })=>{
    // Handle different usage patterns:
    // 1. React Error Boundary: { error, errorInfo } props
    // 2. Manual usage: { errorDetails } prop or no props
    const actualError = error || errorDetails?.error || new Error('Manual error state triggered');
    const actualErrorInfo = errorInfo || errorDetails?.errorInfo || {};
    
    const message = actualError?.message || actualError?.error?.message || 'Unknown error occurred';
    console.log('err', message)
    
    // Comprehensive error tracking to Amplitude
    useEffect(() => {
        const trackErrorToAmplitude = () => {
            try {
                // Parse error object to extract meaningful information
                const parseError = (err) => {
                    if (!err) return { message: 'No error provided', name: 'UnknownError', stack: 'No stack trace' };
                    
                    // Handle different error types
                    if (typeof err === 'string') {
                        return { message: err, name: 'StringError', stack: 'No stack trace' };
                    }
                    
                    if (err instanceof Error) {
                        return {
                            message: err.message || 'No error message',
                            name: err.name || 'Error',
                            stack: err.stack || 'No stack trace',
                            constructor_name: err.constructor?.name || 'Error'
                        };
                    }
                    
                    // Handle API errors or custom error objects
                    if (typeof err === 'object') {
                        return {
                            message: err.message || err.error || err.statusText || JSON.stringify(err),
                            name: err.name || err.type || 'ObjectError',
                            stack: err.stack || 'No stack trace',
                            status: err.status || err.statusCode || null,
                            response_data: err.response?.data ? JSON.stringify(err.response.data) : null,
                            request_url: err.config?.url || err.url || null,
                            request_method: err.config?.method || err.method || null
                        };
                    }
                    
                    // Fallback for unknown error types
                    return {
                        message: String(err),
                        name: 'UnknownError',
                        stack: 'No stack trace'
                    };
                };
                
                const parsedError = parseError(actualError);
                
                // Get comprehensive error details
                const errorTrackingDetails = {
                    // Parsed Error Information
                    error_message: parsedError.message,
                    error_name: parsedError.name,
                    error_stack: parsedError.stack,
                    error_type: parsedError.constructor_name || parsedError.name,
                    
                    // API Error Details (if applicable)
                    ...(parsedError.status && { http_status: parsedError.status }),
                    ...(parsedError.response_data && { response_data: parsedError.response_data }),
                    ...(parsedError.request_url && { request_url: parsedError.request_url }),
                    ...(parsedError.request_method && { request_method: parsedError.request_method }),
                    
                    // Error Boundary Information
                    component_stack: actualErrorInfo?.componentStack || 'No component stack',
                    error_boundary: 'ErrorFallback',
                    is_react_error_boundary: !!(error && errorInfo),
                    is_manual_error_state: !(error && errorInfo),
                    
                    // Page/Context Information
                    page_url: window.location.href,
                    page_pathname: window.location.pathname,
                    page_search: window.location.search,
                    page_hash: window.location.hash,
                    page_title: document.title,
                    
                    // User Context
                    user_logged_in: !!localStorage.getItem('accesstoken'),
                    user_phone: localStorage.getItem('phoneNo') || null,
                    current_currency: localStorage.getItem('current_currency_type') || 'INR',
                    
                    // Browser/Device Information
                    user_agent: navigator.userAgent,
                    browser_language: navigator.language,
                    browser_languages: navigator.languages?.join(', ') || '',
                    browser_platform: navigator.platform,
                    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
                    device_memory: navigator.deviceMemory || 'unknown',
                    connection_type: navigator.connection?.effectiveType || 'unknown',
                    
                    // App State
                    bundle_id: localStorage.getItem('bundleId') || null,
                    brand_id: localStorage.getItem('brandId') || null,
                    return_page: localStorage.getItem('returnToPage') || null,
                    pending_action: localStorage.getItem('pendingAction') || null,
                    
                    // Timing Information
                    error_timestamp: new Date().toISOString(),
                    page_load_time: performance.now(),
                    
                    // Additional Context
                    referrer: document.referrer || 'direct',
                    online_status: navigator.onLine,
                    cookies_enabled: navigator.cookieEnabled,
                    
                    // React Error Boundary specific
                    react_error: true,
                    error_boundary_triggered: true,
                    
                    // Debugging helpers
                    local_storage_keys: Object.keys(localStorage).join(', '),
                    session_storage_keys: Object.keys(sessionStorage).join(', ')
                };
                
                // Ensure all values are serializable for analytics
                const sanitizeForAnalytics = (obj) => {
                    try {
                        return JSON.parse(JSON.stringify(obj));
                    } catch (serializationError) {
                        console.warn('Error serialization failed, using fallback:', serializationError);
                        return {
                            error_message: parsedError.message || 'Serialization failed',
                            error_name: parsedError.name || 'SerializationError',
                            component_name: actualErrorInfo?.componentStack || 'Unknown',
                            timestamp: new Date().toISOString(),
                            serialization_failed: true
                        };
                    }
                };
                
                const sanitizedErrorDetails = sanitizeForAnalytics(errorTrackingDetails);
                
                // Track to Amplitude with all error details
                trackCustomEvent(CUSTOM_EVENTS.REACT_ERROR_BOUNDARY, sanitizedErrorDetails, ANALYTICS_PROVIDERS.AMPLITUDE);
                
                console.log('🚨 Error tracked to Amplitude:', sanitizedErrorDetails);
                
            } catch (trackingError) {
                console.error('Failed to track error to Amplitude:', trackingError);
            }
        };
        
        // Track immediately when component mounts
        trackErrorToAmplitude();
        
    }, [message, actualError, actualErrorInfo, error, errorInfo]);
    
    return(
        // <div>
        //     <p style={{color: 'white'}}>Something went wrong:</p>
        //     <pre style={{color: 'red'}}>{message || ''}</pre>
        // </div>
        <div className={styles.errorScreen}>
            <div className={styles.content}>
                <img className={styles.errorImage} src={errorImage} alt="errorImage" />
                <p className={styles.headerText}>Oops, some error occured!</p>
                <p className={styles.subText}>Please try to reload the app.</p>
                <button onClick={() => {
                    // Track reload attempt
                    trackCustomEvent(CUSTOM_EVENTS.BUTTON_CLICKED, {
                        button_name: 'Reload App',
                        error_recovery_attempt: true,
                        error_message: message || 'Unknown error',
                        page_url: window.location.href,
                        user_logged_in: !!localStorage.getItem('accesstoken')
                    }, ANALYTICS_PROVIDERS.AMPLITUDE);
                    
                    window.location.reload();
                }} className={styles.button} >RELOAD APP</button>
            </div>
        </div>
    )
}

// Helper function for manual error states with context
export const createErrorFallback = (context = {}) => {
    const { 
        errorMessage = 'Data loading failed', 
        errorSource = 'API_ERROR',
        componentName = 'Unknown Component',
        additionalContext = {}
    } = context;
    
    const syntheticError = new Error(errorMessage);
    syntheticError.name = errorSource;
    syntheticError.source = 'manual_error_state';
    
    const errorInfo = {
        componentStack: `Error in ${componentName}`,
        errorBoundary: false,
        ...additionalContext
    };
    
    return <ErrorFallback errorDetails={{ error: syntheticError, errorInfo }} />;
};

// Helper function to create error details from caught exceptions
export const createErrorDetails = (error, context = {}) => {
    const {
        componentName = 'Unknown Component',
        errorContext = 'unknown_operation',
        apiCallsAttempted = [],
        additionalContext = {}
    } = context;
    
    // Serialize error object to ensure it can be properly transmitted
    const serializeError = (err) => {
        if (!err) return null;
        
        // If it's already a plain object, return as is
        if (err.constructor === Object) return err;
        
        // For Error objects, extract key properties
        if (err instanceof Error) {
            return {
                message: err.message,
                name: err.name,
                stack: err.stack,
                constructor: err.constructor.name,
                // Include any custom properties
                ...Object.getOwnPropertyNames(err).reduce((acc, key) => {
                    if (!['message', 'name', 'stack'].includes(key)) {
                        acc[key] = err[key];
                    }
                    return acc;
                }, {})
            };
        }
        
        // For other objects, try to serialize safely
        try {
            return JSON.parse(JSON.stringify(err));
        } catch {
            return { message: String(err), serialization_error: true };
        }
    };
    
    return {
        error: serializeError(error),
        errorInfo: {
            componentStack: `Error in ${componentName}`,
            error_context: errorContext,
            api_calls_attempted: apiCallsAttempted,
            timestamp: new Date().toISOString(),
            ...additionalContext
        }
    };
};